import { Precision } from "../types";

const CSV_IMPORT_SHOW_LOADING_ITEM_NUM = 100;

const CSV_IMPORT_TABLE_CELL_MIN_WIDTH = 190;
const CSV_IMPORT_TABLE_CELL_MAX_WIDTH = 350;

const CSV_DELIMITER = {
  COMMA: ",",
  TAB: "\t"
};

const CSV_SECOND_LEVEL_DELIMITER = ",";

const CSV_EXPORT_FIELDS = [
  "type",
  "displayId",
  "label",
  "summary",
  "narrativePosition",
  "color",
  "super",
  "initials",
  "ongoing",
  "startDate",
  "latestStartDate",
  "duration",
  "earliestEndDate",
  "endDate",
  "blockedByDependencies",
  "links",
  "tags"
];

const CSV_IMPORT_FIELDS = [
  "label",
  "type",
  "color",
  "startDate",
  "endDate",
  "duration",
  "summary",
  "tags",
  "super",
  "internalId",
  "blocking",
  "latestStartDate",
  "earliestEndDate",
  "links"
];

const CSV_FIELD_LABELS = {
  type: "Type",
  displayId: "Display ID",
  label: "Label",
  summary: "Summary",
  narrativePosition: "Narrative Position",
  color: "Color",
  super: "Parent",
  initials: "Initials",
  startDate: "Start Date",
  latestStartDate: "Latest Start Date",
  duration: "Duration",
  earliestEndDate: "Earliest End Date",
  endDate: "End Date",
  ongoing: "Ongoing",
  blockedByDependencies: "Blocked By",
  blocking: "Blocks",
  links: "Links",
  tags: "Tags",
  internalId: "Internal ID"
};

const CSV_LEARN_MORE_LINK_LABEL = "Learn More";

// Interface texts

const CSV_IMPORT_GUIDE_LINK_LABEL = "How-to guide";

const CSV_IMPORT_SETTINGS_LABEL = "Import Settings";

const CSV_IMPORT_HEADER_ROW_CHECK_BOX_LABEL = "My file includes a header row";
const CSV_IMPORT_HEADER_ROW_TOOLTIP =
  "Mark if first row of your file contains headings and not data. If checked, the first row of your file will not be imported.";

const CSV_IMPORT_ADD_TO_NARRATIVE_CHECK_BOX_LABEL = "Add to end of Narrative";
const CSV_IMPORT_ADD_TO_NARRATIVE_TOOLTIP =
  "All imported items which are allowed in the narrative will automatically be added to the end of your narrative.";

const CSV_IMPORT_HEADER_ROW_DESCRIPTION = "Header row will not be imported";

const CSV_IMPORT_DEFAULT_ITEM_TYPE_LABEL = "Default rows to";
const CSV_IMPORT_DEFAULT_ITEM_TYPE_TOOLTIP =
  "Rows will be imported as this item type, unless overridden by a Type column.";

const CSV_IMPORT_DEFAULT_ITEM_TYPE_DESCRIPTION = "Include a type column to import multiple item types";

const CSV_IMPORT_DATE_FORMAT_LABEL = "Date format";
const CSV_IMPORT_DATE_FORMAT_TOOLTIP = "The format to use for importing Start Date and End Date columns.";

const CSV_IMPORT_DATE_FORMAT_CHECK_BOX_LABEL = "2 digits year become 20xx";
const CSV_IMPORT_DATE_FORMAT_CHECK_BOX_TOOLTIP =
  "If the Start or End Date columns contains 2-digit years, these will be converted to 4-digit years in the current century. E.g. '23 Sep 15' will be converted to '23 Sep 2015'.";

const CSV_IMPORT_PREVIEW_TABLE_LABEL = "Select columns to import";

const CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT = "CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT";
const CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL = "Don't Import";

const CSV_IMPORT_COLUMN_INFO = {
  color: `Matches pre-existing color names or hexadecimal codes.`,
  endDate: "Prioritise over duration if both are set",
  duration: "Default to",
  tags: "Create new tags if no matching tag found",
  internalId: `Will update existing items with matching IDs from previous imports`,
  blocking: `Matches item labels or internal IDs`,
  relationItem: "Create new items if no matching item found"
};

const CSV_IMPORT_COLUMN_INFO_TOOLTIP = {
  endDate:
    "If importing items with Start Date, End Date, and Duration columns set, the end date will take priority over the duration.",
  duration:
    "If the Duration column contains numeric values, they will be imported as this unit type. E.g. If set to hours, '5' would be converted to '5 hours'.",
  internalId:
    "An alphanumeric ID that matches an internal ID from an earlier import, or the display ID of existing items in your timeline. The latter can be found in the Display ID column when exporting your existing timeline as a CSV/TSV file.",
  blocking:
    "Internal IDs are alphanumeric IDs that match existing items in your timeline. These can be found in the Display ID column when exporting your existing timeline as a CSV/TSV file.",
  relationItem:
    "If any values in the column do not match an existing item's label or internal ID, a new item will be created."
};

const CSV_IMPORT_DURATION_PRECISIONS = [
  { value: Precision.Year, label: "Year" },
  { value: Precision.Month, label: "Month" },
  { value: Precision.Day, label: "Day" },
  { value: Precision.Hour, label: "Hour" },
  { value: Precision.Minute, label: "Minute" },
  { value: Precision.Second, label: "Second" }
];

const CAN_IMPORT_FIELD = "canImport";

export {
  CAN_IMPORT_FIELD,
  CSV_DELIMITER,
  CSV_EXPORT_FIELDS,
  CSV_FIELD_LABELS,
  CSV_IMPORT_ADD_TO_NARRATIVE_CHECK_BOX_LABEL,
  CSV_IMPORT_ADD_TO_NARRATIVE_TOOLTIP,
  CSV_IMPORT_COLUMN_INFO,
  CSV_IMPORT_COLUMN_INFO_TOOLTIP,
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT,
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL,
  CSV_IMPORT_DATE_FORMAT_CHECK_BOX_LABEL,
  CSV_IMPORT_DATE_FORMAT_CHECK_BOX_TOOLTIP,
  CSV_IMPORT_DATE_FORMAT_LABEL,
  CSV_IMPORT_DATE_FORMAT_TOOLTIP,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_DESCRIPTION,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_LABEL,
  CSV_IMPORT_DEFAULT_ITEM_TYPE_TOOLTIP,
  CSV_IMPORT_DURATION_PRECISIONS,
  CSV_IMPORT_FIELDS,
  CSV_IMPORT_GUIDE_LINK_LABEL,
  CSV_IMPORT_HEADER_ROW_CHECK_BOX_LABEL,
  CSV_IMPORT_HEADER_ROW_DESCRIPTION,
  CSV_IMPORT_HEADER_ROW_TOOLTIP,
  CSV_IMPORT_PREVIEW_TABLE_LABEL,
  CSV_IMPORT_SETTINGS_LABEL,
  CSV_IMPORT_SHOW_LOADING_ITEM_NUM,
  CSV_IMPORT_TABLE_CELL_MAX_WIDTH,
  CSV_IMPORT_TABLE_CELL_MIN_WIDTH,
  CSV_LEARN_MORE_LINK_LABEL,
  CSV_SECOND_LEVEL_DELIMITER
};
